import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiDelete } from '@/lib/api/client';
import { apiFetch } from '@/lib/apiClient';

interface Member {
  id: string;
  email: string;
  name?: string;
  role: string;
  joinedAt: number;
}

interface Props {
  workspaceId: string;
  token: string;
}

export function MembersSection({ workspaceId, token }: Props) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await apiGet<{ members: Member[] }>(`/workspaces/${workspaceId}/members`);
      setMembers(res.data.members ?? []);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleRoleChange = async (memberId: string, role: string) => {
    setError(null);
    try {
      await apiFetch(`workspaces/${workspaceId}/members/${memberId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
        authToken: token,
      });
      fetchMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!confirm('Remove this member from the workspace?')) return;
    setError(null);
    try {
      await apiDelete(`/workspaces/${workspaceId}/members/${memberId}`);
      fetchMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setInviting(true);
    setError(null);
    try {
      await apiPost(`/workspaces/${workspaceId}/invites`, { email, role: 'member' });
      setInviteEmail('');
      fetchMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  if (loading) return <p className="ws-settings-muted">Loading members...</p>;

  return (
    <div>
      <div className="ws-settings-section-header">
        <h2>Members ({members.length})</h2>
      </div>

      <div className="ws-settings-table">
        {members.map(member => (
          <div key={member.id} className="ws-settings-row">
            <div className="ws-settings-row-info">
              <span className="ws-settings-row-name">{member.name || member.email.split('@')[0]}</span>
              <span className="ws-settings-row-email">{member.email}</span>
            </div>
            <div className="ws-settings-row-actions">
              {member.role === 'owner' ? (
                <span className="ws-settings-badge">Owner</span>
              ) : (
                <>
                  <select
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                    className="ws-settings-select"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Read & Write</option>
                    <option value="read_only">Read Only</option>
                  </select>
                  <button onClick={() => handleRemove(member.id)} className="ws-settings-remove">Remove</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleInvite} className="ws-settings-invite">
        <input
          type="email"
          placeholder="Invite by email..."
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          className="ws-settings-input"
        />
        <button type="submit" disabled={inviting} className="ws-settings-btn-primary">
          {inviting ? 'Sending...' : 'Invite'}
        </button>
      </form>
      {error && <p className="ws-settings-error">{error}</p>}
    </div>
  );
}
